/**
 * Customer & Shipping Address Models
 * Matches the Firestore schema specification
 */

export const INDIAN_STATES = [
  { code: "33", name: "Tamil Nadu" },
  { code: "29", name: "Karnataka" },
  { code: "32", name: "Kerala" },
  { code: "36", name: "Telangana" },
  { code: "37", name: "Andhra Pradesh" },
  { code: "27", name: "Maharashtra" },
  { code: "24", name: "Gujarat" },
  { code: "07", name: "Delhi" },
  { code: "09", name: "Uttar Pradesh" },
  { code: "19", name: "West Bengal" },
  { code: "03", name: "Punjab" },
  { code: "08", name: "Rajasthan" },
  { code: "23", name: "Madhya Pradesh" },
  { code: "10", name: "Bihar" },
  { code: "99", name: "Other Territory" }
];

export function createDefaultCustomer(initialPartyType = "customer") {
  return {
    partyType: initialPartyType, // 'customer' | 'vendor' | 'both'
    name: "",
    mobile: "",
    email: "",
    gstin: "",
    stateCode: "33",
    state: "Tamil Nadu",
    city: "",
    billingAddress: {
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "Tamil Nadu",
      stateCode: "33",
      pincode: ""
    },
    openingBalance: 0,
    companyIds: [], // companies this party works with (empty on old records = all companies)
    openingBalances: {}, // opening due typed in by hand, per company: { [companyId]: amount }
    customPrices: {}, // Stores customer-specific item price overrides: { [itemId]: rate }
    bankDetails: {
      bankName: "",
      accountNumber: "",
      ifscCode: "",
      upiId: ""
    },
    status: "active"
  };
}

export function createDefaultShippingAddress() {
  return {
    label: "Warehouse / Branch",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "Tamil Nadu",
    stateCode: "33",
    pincode: "",
    contactPerson: "",
    contactMobile: "",
    isDefault: false
  };
}

export function extractStateCodeFromGstin(gstin = "") {
  if (gstin && gstin.trim().length >= 2) {
    const code = gstin.trim().substring(0, 2);
    const match = INDIAN_STATES.find((s) => s.code === code);
    if (match) return match;
  }
  return { code: "33", name: "Tamil Nadu" };
}

/**
 * Returns the customer-specific price for an item, falling back to item's default price
 */
export function getCustomerPriceForItem(customer, item) {
  if (!item) return 0;
  const defaultRate = Number(item.basicPrice || item.sellingPrice || 0);
  if (!customer || !customer.customPrices) return defaultRate;

  const itemId = item.id;
  if (itemId && customer.customPrices[itemId] !== undefined && customer.customPrices[itemId] !== null) {
    const customRate = Number(customer.customPrices[itemId]);
    if (!isNaN(customRate) && customRate >= 0) {
      return customRate;
    }
  }
  return defaultRate;
}

export function isVendor(party) {
  if (!party) return false;
  return party.partyType === "vendor" || party.partyType === "both";
}

export function isCustomer(party) {
  if (!party) return true;
  // Existing records without partyType are treated as customers
  return !party.partyType || party.partyType === "customer" || party.partyType === "both";
}

/* ---------- Parties per company ---------- */

/** A party belongs to a company when ticked for it. Old records not yet assigned count for every company. */
export function partyInCompany(party, companyId) {
  if (!party) return false;
  const ids = party.companyIds;
  if (!Array.isArray(ids) || ids.length === 0) return true;
  return !companyId || ids.includes(companyId);
}

/** Opening due typed in by hand for one company. */
export function openingFor(party, companyId) {
  return Number(party?.openingBalances?.[companyId] || 0);
}

